

import React, { useContext } from "react";

import {
    List,
    Datagrid,
    TextField,
    EditButton,
    Edit,
    SimpleForm,
    ReferenceInput,
    ReferenceArrayInput,
    AutocompleteInput,
    AutocompleteArrayInput,
    DateInput,
    useGetList,
    SelectInput,
} from "react-admin";

import { AuthContext } from "../../context/AuthContext";



import { Box, Typography } from "@mui/material";
import { useWatch } from "react-hook-form";

export const UserProfileList = () => (
    <List>
        <Datagrid rowClick="edit">
            <TextField source="id" />
            <TextField source="user.username" label="Пользователь" />
            <TextField source="tariff_plan.name" label="Тариф" />
            <TextField source="main_organization.name" label="Основная организация" />
            <TextField source="subscription_start" label="Начало подписки" />
            <TextField source="subscription_end" label="Конец подписки" />
            <EditButton />
        </Datagrid>
    </List>
);

const orgOptionText = (choice) => {
    if (!choice) return "";
    return `${choice.name} (${choice.bin})`;
};

const governmentInstitutionOptionText = (choice) => {
    if (!choice) return "";
    return `${choice.name} (${choice.gov_code})`;
};

const tariffOptionText = (choice) => {
    if (!choice) return "";
    if (choice.bin_limit === null || choice.bin_limit === undefined) {
        return `${choice.name} (безлимит)`;
    }
    return `${choice.name} (${choice.bin_limit} БИН)`;
};

const TariffUsageInfo = () => {
    const tariffPlanId = useWatch({ name: "tariff_plan_id" });
    const mainOrganizationId = useWatch({ name: "main_organization_id" });
    const additionalOrganizationIds = useWatch({ name: "additional_organization_ids" }) || [];

    const { data: tariffPlans = [] } = useGetList("users/tariff-plans", {
        pagination: { page: 1, perPage: 100 },
        sort: { field: "id", order: "ASC" },
        filter: {},
    });

    const selectedTariff = tariffPlans.find((t) => String(t.id) === String(tariffPlanId));
    const usedBins = (mainOrganizationId ? 1 : 0) + additionalOrganizationIds.length;

    let message = "Сначала выберите тариф";
    let color = "#6b7280";
    let secondary = "";

    if (selectedTariff) {
        if (selectedTariff.bin_limit === null || selectedTariff.bin_limit === undefined) {
            message = `Использовано БИНов: ${usedBins} / Без ограничений`;
            color = "#2e7d32";
            secondary = "Для этого тарифа лимит не применяется.";
        } else {
            const remaining = selectedTariff.bin_limit - usedBins;
            message = `Использовано БИНов: ${usedBins} / ${selectedTariff.bin_limit}`;

            if (remaining <= 0) {
                color = "#9a6700";
                secondary = "Лимит БИНов достигнут.";
            } else if (remaining === 1) {
                color = "#b7791f";
                secondary = "Остался 1 доступный слот.";
            } else {
                color = "#2e7d32";
                secondary = `Осталось доступных слотов: ${remaining}.`;
            }
        }
    }

    return (
        <Box
            sx={{
                mt: 1,
                mb: 2,
                p: 1.5,
                borderRadius: 2,
                backgroundColor: "#f8fafc",
                border: "1px solid #e5e7eb",
            }}
        >
            <Typography sx={{ fontWeight: 600, color }}>
                {message}
            </Typography>
            {secondary ? (
                <Typography sx={{ mt: 0.5, fontSize: 13, color: "#6b7280" }}>
                    {secondary}
                </Typography>
            ) : null}
        </Box>
    );
};


export const UserProfileEdit = () => {
    const { role } = useContext(AuthContext);

    return (
        <Edit>
            <SimpleForm
                onKeyDown={(e) => {
                    if (e.key === "Enter" && e.target.tagName !== "TEXTAREA") {
                        e.preventDefault();
                    }
                }}
            >
                <TextField source="user.username" label="Пользователь" />

                {role === "admin" && (
                    <SelectInput
                        source="role"
                        label="Роль"
                        choices={[
                            { id: "user", name: "Пользователь" },
                            { id: "manager", name: "Менеджер" },
                            { id: "admin", name: "Админ" },
                        ]}
                        fullWidth
                    />
                )}

                <ReferenceInput
                    source="tariff_plan_id"
                    reference="users/tariff-plans"
                    label="Тариф"
                >
                    <AutocompleteInput
                        optionText={tariffOptionText}
                        fullWidth
                        clearOnBlur={false}
                        filterToQuery={(searchText) => ({ q: searchText })}
                    />
                </ReferenceInput>

                <TariffUsageInfo />

                <ReferenceInput
                    source="main_organization_id"
                    reference="users/organizations"
                    label="Основная организация"
                >
                    <AutocompleteInput
                        optionText={orgOptionText}
                        fullWidth
                        emptyText="Не выбрано"
                        clearOnBlur={false}
                        filterToQuery={(searchText) => ({ q: searchText })}
                    />
                </ReferenceInput>

                <ReferenceArrayInput
                    source="additional_organization_ids"
                    reference="users/organizations"
                    label="Дополнительные организации"
                >
                    <AutocompleteArrayInput
                        optionText={orgOptionText}
                        fullWidth
                        clearOnBlur={false}
                        filterToQuery={(searchText) => ({ q: searchText })}
                    />
                </ReferenceArrayInput>

                <Typography sx={{ mt: -1, mb: 2, fontSize: 13, color: "#6b7280" }}>
                    Поиск работает по названию и БИН. Удаление — по одному элементу через крестик.
                </Typography>

                <ReferenceArrayInput
                    source="government_institution_ids"
                    reference="users/government-institutions"
                    label="Государственные учреждения"
                >
                    <AutocompleteArrayInput
                        optionText={governmentInstitutionOptionText}
                        fullWidth
                        clearOnBlur={false}
                        filterToQuery={(searchText) => ({ q: searchText })}
                    />
                </ReferenceArrayInput>

                <Typography sx={{ mt: -1, mb: 2, fontSize: 13, color: "#6b7280" }}>
                    Поиск работает по названию и коду ГУ. Удаление — по одному элементу через крестик.
                </Typography>

                <DateInput source="subscription_start" label="Начало подписки" />
                <DateInput source="subscription_end" label="Конец подписки" />
            </SimpleForm>
        </Edit>
    );
};

